import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { toMultiDropdownOptions } from 'shared/utils/formUtils';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import { withLocalePrefix } from 'shared/utils/translation';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row.relevance');

// MUI Portal defers container resolution by one render cycle, so on the initial
// mount downshift's ref check fires before the menu list is in the DOM. This is
// a false positive -- the ref is set correctly one frame later. Wrapping
// getMenuProps with suppressRefError lets us keep the flyout in a portal
// (avoiding scroll-container clipping) without the console noise.
/* eslint-disable @typescript-eslint/no-explicit-any */
const withSuppressedRefError = <
  T extends { getMenuProps: (...args: any[]) => any }
>(
  props: T
): T => ({
  ...props,
  getMenuProps: (...args: Parameters<T['getMenuProps']>) =>
    props.getMenuProps(args[0], { ...args[1], suppressRefError: true }),
});
/* eslint-enable @typescript-eslint/no-explicit-any */

const JobsDepartmentsOfficesFilters = () => {
  const {
    apiClient,
    similarJobOptions,
    hiringPlanDepartment,
    hiringPlanOffices,
  } = useRediscoveryTabContext();
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const [jobs, departments, offices] = watch([
    'jobs',
    'departments',
    'offices',
  ]);

  const fetchJobItems = useCallback(
    async (term: string) => {
      const queryKey = ['jobs', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }
      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedItems = await apiClient.fetchJobs(term);
          return toMultiDropdownOptions(fetchedItems);
        },
      });
      return { items };
    },
    [apiClient, queryClient]
  );

  const fetchDepartmentItems = useCallback(
    async (term: string) => {
      const queryKey = ['departments', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }
      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedItems = await apiClient.fetchDepartments(term);
          return toMultiDropdownOptions(fetchedItems);
        },
      });
      return { items };
    },
    [apiClient, queryClient]
  );

  const fetchOfficeItems = useCallback(
    async (term: string) => {
      const queryKey = ['offices', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }
      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedItems = await apiClient.fetchOffices(term);
          return toMultiDropdownOptions(fetchedItems);
        },
      });
      return { items };
    },
    [apiClient, queryClient]
  );

  const jobsDropdownProps = useMultiDropdown({
    fetchItems: fetchJobItems,
    onSelectedItemsChange: (e) =>
      setValue('jobs', e.selectedItems, { shouldDirty: true }),
    selectedItems: jobs,
    preventLoadOnMount: true,
  });

  const departmentsDropdownProps = useMultiDropdown({
    fetchItems: fetchDepartmentItems,
    onSelectedItemsChange: (e) =>
      setValue('departments', e.selectedItems, { shouldDirty: true }),
    selectedItems: departments,
    preventLoadOnMount: true,
  });

  const officesDropdownProps = useMultiDropdown({
    fetchItems: fetchOfficeItems,
    onSelectedItemsChange: (e) =>
      setValue('offices', e.selectedItems, { shouldDirty: true }),
    selectedItems: offices,
    preventLoadOnMount: true,
  });

  const addSimilarJobs = () => {
    setValue('jobs', similarJobOptions, { shouldDirty: true });
  };

  const addHiringPlanDepartment = () => {
    setValue('departments', [hiringPlanDepartment], { shouldDirty: true });
  };

  const addHiringPlanOffices = () => {
    setValue('offices', hiringPlanOffices, { shouldDirty: true });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <Stack
          spacing={1}
          alignItems="flex-start"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <MultiDropdown
            dropdownProps={withSuppressedRefError(jobsDropdownProps)}
          >
            <MultiDropdown.Field
              data-provides="talent-rediscovery-relevance-jobs-dropdown"
              label={t('jobs_label')}
              condensedUnitName={t('jobs_label')}
            />
            <MultiDropdown.Flyout />
          </MultiDropdown>
          <Tooltip
            label={
              similarJobOptions.length === 0
                ? t('add_similar_jobs_tooltip')
                : undefined
            }
            ariaLabel={t('add_similar_jobs_tooltip')}
          >
            <TertiaryButton
              onClick={addSimilarJobs}
              size="sm"
              disabled={!similarJobOptions.length}
            >
              {t('add_similar_jobs')}
            </TertiaryButton>
          </Tooltip>
        </Stack>

        <Stack
          spacing={1}
          alignItems="flex-start"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <MultiDropdown
            dropdownProps={withSuppressedRefError(departmentsDropdownProps)}
          >
            <MultiDropdown.Field
              data-provides="talent-rediscovery-relevance-departments-dropdown"
              label={t('departments_label')}
              condensedUnitName={t('departments_label')}
            />
            <MultiDropdown.Flyout />
          </MultiDropdown>
          <Tooltip
            label={!hiringPlanDepartment && t('no_department_tooltip')}
            ariaLabel={t('no_department_tooltip')}
          >
            <TertiaryButton
              onClick={addHiringPlanDepartment}
              size="sm"
              disabled={!hiringPlanDepartment}
            >
              {t('add_current_departments')}
            </TertiaryButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack spacing={1} alignItems="flex-start">
        <FullWidth>
          <MultiDropdown
            dropdownProps={withSuppressedRefError(officesDropdownProps)}
          >
            <MultiDropdown.Field
              data-provides="talent-rediscovery-relevance-offices-dropdown"
              label={t('offices_label')}
              condensedUnitName={t('offices_label')}
            />
            <MultiDropdown.Flyout />
          </MultiDropdown>
        </FullWidth>
        <Tooltip
          label={!hiringPlanOffices.length && t('no_offices_tooltip')}
          ariaLabel={t('no_offices_tooltip')}
        >
          <TertiaryButton
            onClick={addHiringPlanOffices}
            size="sm"
            disabled={!hiringPlanOffices.length}
          >
            {t('add_current_offices')}
          </TertiaryButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default JobsDepartmentsOfficesFilters;
